import { useCallback, useEffect, useState } from 'react';
import { LoginPage } from '@tmr/login-kit';
import {
  useTmrAuthz,
  useClerkLoginHandlers,
  hasPermission,
  portalPermissions,
  emailLinkArrivalError,
  SsoCallback,
} from '@tmr/auth';
import { describeAuthError, getLoginKitMessages } from '@tmr/login-kit';
import { api } from '@tmr/api-client';
import { useDesignTheme } from '@tmr/design-system';
import { portal } from './portal';
import { Portal } from './portal/Portal';
import { LocalLoginPage, isLocalLoginRoute } from './LocalLoginPage';
import { AcceptInvitePage, isAcceptInviteRoute } from './AcceptInvitePage';

/**
 * Portal do Administrador — DESKTOP-FIRST. Console do operador.
 * Autenticação/autorização via Clerk: UMA aplicação Clerk atende os quatro portais.
 * Assim que o usuário está autenticado e autorizado ao portal admin, renderizamos o
 * `<Portal>` (shell + Empresas/Contratos/Partes/Perfil).
 * O visual de login é o `<LoginPage>` compartilhado.
 *
 * Quem manda sobre "estou logado?" é a **sessão TMR** (cookie httpOnly), não o Clerk — é o que faz o
 * acesso local (break-glass) sobreviver a um refresh de página, já que ali não há IdP a quem perguntar.
 */
export default function App() {
  const auth = useTmrAuthz();
  const handlers = useClerkLoginHandlers();
  const [signingOut, setSigningOut] = useState(false);

  // Um link mágico aberto no navegador errado volta para cá com `__clerk_status` na URL e nenhuma sessão.
  // Sem ler isso, a pessoa clica no link, chega no login e não faz ideia do que aconteceu — que foi o
  // relato do `client_mismatch`. Lido uma vez: o estado é da chegada, não muda enquanto a tela vive.
  const [arrivalError] = useState(() => emailLinkArrivalError());
  const messages = getLoginKitMessages('pt-BR');
  // The site design system has its own login: the stage (29). Same form, same states.
  const loginVariant = useDesignTheme() === 'site' ? 'stage' : 'classic';

  const signOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await auth.signOut();
    } finally {
      setSigningOut(false);
    }
  }, [auth]);

  // A dead session (401 on any call) is rebuilt in place, once, here — no screen implements its own
  // redirect. Rebuilding rather than signing out is the point: the TMR cookie lasts 30 minutes and the
  // Clerk one lasts far longer, so the ordinary cause is a lapsed cookie with the identity still in
  // hand. Signing out instead sent the operator to a login form that Clerk then refused to serve,
  // because it was still signed in — the "sessão expirada que não reloga". When the rebuild fails, the
  // session hook reports it and the screens below take over.
  useEffect(() => {
    api.onUnauthorized(() => {
      void auth.recoverSession();
    });
  }, [auth]);

  if (auth.status === 'sso-callback') return <SsoCallback />;

  // Accepting an invitation, before any session gate: the person arriving here has no TMR session yet —
  // creating it is the whole point — and the Clerk ticket in the URL is single-use, so bouncing them to
  // the login screen first would burn the invitation (06 §6.1).
  if (isAcceptInviteRoute()) return <AcceptInvitePage />;

  // Break-glass sign-in, on its own discreet route (05 I3). Only offered while signed out: an operator
  // who already has a session has nothing to break the glass for.
  if (auth.status !== 'signed-in' && isLocalLoginRoute()) {
    return <LocalLoginPage onSubmit={auth.signInLocally} />;
  }

  if (auth.status === 'signed-in' && auth.session) {
    const operator = auth.session;
    const required = portalPermissions('admin', auth.source);
    // Real gate: access requires the backend-resolved authorization.admin permission (05/06).
    const authorized = hasPermission(auth.permissions, required);

    if (authorized) {
      // The whole permission array goes down, not a pre-chewed boolean: screens gate their own actions
      // (editing an Empresa needs company.update), and re-reading authorization inside the tree would
      // mean a second session exchange.
      return (
        <Portal
          operator={operator}
          permissions={auth.permissions}
          scope={auth.scope}
          capabilities={auth.capabilities}
          activeContext={auth.activeContext}
          onSignOut={signOut}
        />
      );
    }

    // A autorização não chegou. Antes isto caía na mesma tela de "você não tem acesso", porque uma
    // lista de permissions vazia é indistinguível de uma que não carregou — e a única ação oferecida
    // era sair. Um pedido que falhou se resolve pedindo de novo.
    if (auth.authzError) {
      return (
        <LoginPage
          portal={portal}
          responsive="desktop-first"
          variant={loginVariant}
          brandSide="left"
          blocked={{
            title: 'Não foi possível carregar seus acessos',
            message: <>Sua sessão está válida. Foi a consulta dos seus acessos que falhou.</>,
            onRetry: auth.reloadAuthorization,
            onSignOut: signOut,
            signOutLabel: signingOut ? 'Saindo…' : 'Sair',
          }}
        />
      );
    }

    // Autenticado, mas sem a permission do portal → o aviso vive na PRÓPRIA tela
    // de login (estado `blocked`), nunca numa tela separada fora do padrão.
    return (
      <LoginPage
        portal={portal}
        responsive="desktop-first"
        variant={loginVariant}
        brandSide="left"
        blocked={{
          title: operator.name || operator.email || 'Acesso não autorizado',
          message: <>Esta conta não tem acesso ao {portal.name}. Peça a liberação a quem administra o portal.</>,
          onSignOut: signOut,
          signOutLabel: signingOut ? 'Saindo…' : 'Sair',
        }}
      />
    );
  }

  // Sem sessão TMR, mas o navegador ainda tem a identidade: só o cookie caducou. NÃO desenhar o
  // formulário aqui é a correção — o Clerk recusa autenticar um cliente que já tem sessão, então toda
  // tentativa voltava como "e-mail ou senha incorretos" e a única saída era recarregar a página, que
  // foi o que fez o defeito parecer ser sobre o caminho da URL.
  if (auth.status === 'signed-out' && auth.canRecoverSession) {
    return (
      <LoginPage
        portal={portal}
        responsive="desktop-first"
        variant={loginVariant}
        brandSide="left"
        blocked={{
          title: 'Sua sessão expirou',
          message: auth.sessionError ? (
            <>Não foi possível reconectar: {auth.sessionError.message}</>
          ) : (
            <>Você continua identificado neste navegador — é só reconectar para continuar.</>
          ),
          onRetry: () => void auth.recoverSession(),
          retryLabel: 'Reconectar',
          onSignOut: signOut,
          signOutLabel: signingOut ? 'Saindo…' : 'Entrar com outra conta',
        }}
      />
    );
  }

  // `loading` = sessão ainda resolvendo (cookie e, se preciso, o Clerk) → o próprio login mostra um
  // loader on-brand (busy), em vez de uma tela de "Verificando sessão" separada.
  //
  // Uma falha de sessão sem identidade no navegador vira aviso ACIMA do formulário, não no lugar dele:
  // aqui tentar de novo é digitar de novo. A mensagem sai crua porque ela já é a do backend, em PT-BR —
  // traduzi-la por status diria "e-mail ou senha incorretos" para uma falha que não é de credencial.
  const notice = auth.sessionError
    ? { tone: 'error' as const, message: auth.sessionError.message }
    : arrivalError
      ? { tone: 'error' as const, message: describeAuthError(arrivalError, messages.notice, messages.notice.loginError) }
      : null;
  return (
    <LoginPage
      portal={portal}
      responsive="desktop-first"
      variant={loginVariant}
      brandSide="left"
      busy={auth.status === 'loading'}
      busyLabel="Verificando sessão"
      notice={notice}
      onSubmit={handlers.onSubmit}
      onSso={handlers.onSso}
      onMagicLink={handlers.onMagicLink}
      onResetPassword={handlers.onResetPassword}
      onResetVerify={handlers.onResetVerify}
    />
  );
}
